import { supabase } from '../config/supabase'
import { getSelectedSalonId } from '../utils/salonScope'

/**
 * Fetch the currently selected salon
 */
export const getSalon = async (salonId = getSelectedSalonId()) => {
  try {
    console.log('🔍 Fetching salon data from Supabase...')
    let query = supabase
      .from('salons')
      .select('id, name, description, business_hours, additional_info, location, email, phone, country_code, currency, city, slug, image_urls, rating, total_reviews')
      .eq('is_active', true)

    if (salonId) {
      query = query.eq('id', salonId)
    } else {
      query = query.order('created_at', { ascending: true }).limit(1)
    }

    const { data, error } = await query.maybeSingle()

    if (error) {
      // If no salon exists yet, return null gracefully
      if (error.code === 'PGRST116') {
        console.warn('⚠️ No salon record found in database (PGRST116)')
        return { data: null, error: null }
      }
      console.error('❌ Error fetching salon:', error)
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      })
      throw error
    }
    
    if (!data) {
      console.warn('⚠️ Salon query returned no data')
      return { data: null, error: null }
    }
    
    console.log('✅ Salon data fetched:', {
      id: data.id,
      name: data.name,
      hasDescription: !!data.description,
      hasBusinessHours: !!data.business_hours,
      hasAdditionalInfo: !!data.additional_info
    })
    
    return { data, error: null }
  } catch (error) {
    console.error('❌ Exception in getSalon:', error)
    return { data: null, error }
  }
}

/**
 * Fetch all salons (if you have multiple locations)
 */
export const getSalons = async () => {
  try {
    const { data, error } = await supabase
      .from('salons')
      .select('id, name, description, business_hours, additional_info, location, city, slug, email, phone, country_code, currency, is_active')
      .eq('is_active', true)
      .order('created_at', { ascending: true })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching salons:', error)
    return { data: null, error }
  }
}

/**
 * Create a new salon
 */
export const createSalon = async (salonData) => {
  try {
    const { data, error } = await supabase
      .from('salons')
      .insert([salonData])
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error creating salon:', error)
    return { data: null, error }
  }
}

/**
 * Update salon information
 */
export const updateSalon = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('salons')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error updating salon:', error)
    return { data: null, error }
  }
}

/**
 * Delete a salon
 */
export const deleteSalon = async (id) => {
  try {
    const { error } = await supabase
      .from('salons')
      .delete()
      .eq('id', id)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error deleting salon:', error)
    return { error }
  }
}

